import { Box } from "@govlaunch/core";
import { ProductCard } from "@govlaunch/product";
import { Flex, PageContainer, PageGridContainer } from "@govlaunch/web";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useSelfie } from "~/components/auth/Auth";
import ShowMoreButtonWithIcon from "~/components/form/resources/ShowMoreButtonWithIcon";
import BackToTop from "~/components/qol/BackToTop";
import ProductsPageSeo from "~/components/seo/ProductsPageSeo";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import { useIntlLocale } from "~/lib/withIntl";
import NoResultsFound from "~/src/shared/components/NoResultsFound";
import stringOrFirst from "~/src/utils/stringOrFirst";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
import TranslationButton from "~/src/components/translation/TranslationButton";
import SearchProductsQuery from "~/src/components/product/graphql/SearchProductsQuery";
import ToggleProductStackMutation from "~/src/components/product/graphql/ToggleProductStackMutation";
import {
  ISearchProductsQuery,
  ISearchProductsQueryVariables,
} from "~/src/components/product/graphql/__generated__/SearchProductsQuery.generated";
import {
  IToggleProductStackMutation,
  IToggleProductStackMutationVariables,
} from "~/src/components/product/graphql/__generated__/ToggleProductStackMutation.generated";
import ProductsListingPageFilters from "~/src/components/product/ProductsListingPageFilters";
import ProductsListingPageHeader from "~/src/components/product/ProductsListingPageHeader";

export default function ProductsListingPage() {
  const viewer = useSelfie();
  const [pageCount, setPageCount] = useState(1);
  const router = useRouter();
  const { locale } = useIntlLocale();

  const variables: ISearchProductsQueryVariables = {
    name: stringOrFirst(router.query.name),
    tags: Array.isArray(router.query.tags) ? router.query.tags : router.query.tags ? [router.query.tags] : [],
    cursor: null,
    limit: 24,
  };

  const { loading, data, fetchMore, networkStatus } = useQuery<ISearchProductsQuery, ISearchProductsQueryVariables>(
    SearchProductsQuery,
    {
      variables,
      notifyOnNetworkStatusChange: true,
    },
  );

  const [toggleProductStack] = useMutation<IToggleProductStackMutation, IToggleProductStackMutationVariables>(
    ToggleProductStackMutation,
  );

  const onClickLoadMore = async () => {
    await fetchMore({
      variables: {
        cursor: data?.searchProducts?.pageInfo?.endCursor,
      },
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.searchProducts) {
          return previousQueryResult;
        }

        return {
          ...fetchMoreResult,
          searchProducts: {
            ...fetchMoreResult.searchProducts,
            items: [
              ...(previousQueryResult.searchProducts?.items || []),
              ...(fetchMoreResult.searchProducts.items || []),
            ],
          },
        };
      },
    });

    setPageCount(pageCount + 1);
  };

  return (
    <NavbarLayout>
      <ProductsPageSeo />

      <Box pb={24}>
        <ProductsListingPageFilters />

        <ProductsListingPageHeader />

        {data?.searchProducts?.items?.length === 0 ? (
          <PageContainer>
            <NoResultsFound mt={32} />
          </PageContainer>
        ) : (
          <PageGridContainer columns={[1, 1, 2, 4]} spacingY={6}>
            {(data?.searchProducts?.items || []).map((product, index) => {
              const translationLinkWrap = (slug: string) => {
                return (
                  <TranslationButton
                    paddingY="4px"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      router.push(`/products/${slug}?lang=${locale}`);
                    }}
                  />
                );
              };

              return (
                <Link key={product._id} href={`/products/${product.slug}`} passHref={true}>
                  <ProductCard
                    data-intercom-target={index === 0 ? "First product" : undefined}
                    product={product}
                    canToggleInStack={viewer ? viewer.__typename === "GovernmentUser" : false}
                    onToggleInStack={(product) => {
                      if (viewer && viewer.__typename === "GovernmentUser") {
                        toggleProductStack({
                          refetchQueries: refetchQueries(["Government"]),
                          variables: {
                            productId: product._id,
                            governmentId: viewer.government._id,
                          },
                        });
                      }
                    }}
                    translationLinkWrap={
                      locale !== "en-US" && product.allowRequestTranslation ? translationLinkWrap : undefined
                    }
                  />
                </Link>
              );
            })}
          </PageGridContainer>
        )}

        {data?.searchProducts?.pageInfo?.hasNextPage && (
          <Flex justifyContent="center" mt={6}>
            <ShowMoreButtonWithIcon
              onClick={onClickLoadMore}
              loading={loading && networkStatus === NetworkStatus.fetchMore}
            />
          </Flex>
        )}
      </Box>

      <BackToTop />
    </NavbarLayout>
  );
}
